/**
 * Keeps the page, sort, filters and search term in the address bar so /management/users
 * survives a reload and can be linked to.
 *
 * The param names match what Management::UsersController reads, so the URL the
 * user sees is the same one the JSON request uses. replaceState rather than
 * pushState, mirroring lib/admin-kit/hooks/useAdminResource — paging and
 * filtering are not treated as navigation history.
 */

export const DEFAULT_SORT_ORDER = 'ASC';

export const EMPTY_FILTERS = {
  accountIds: [],
  permissions: [],
  jobRoles: [],
};

// Filter key in state <-> array param the controller reads.
const FILTER_PARAMS = {
  accountIds: 'account_ids',
  permissions: 'permissions',
  jobRoles: 'job_roles',
};

export const readUrlState = () => {
  const params = new URLSearchParams(window.location.search);
  const page = parseInt(params.get('page'), 10);
  const sortBy = params.get('sort_by') || null;

  let sortOrder = null;
  if (sortBy) {
    sortOrder =
      params.get('sort_order') === 'DESC' ? 'DESC' : DEFAULT_SORT_ORDER;
  }

  const filters = { ...EMPTY_FILTERS };
  Object.entries(FILTER_PARAMS).forEach(([key, param]) => {
    filters[key] = params.getAll(`${param}[]`);
  });

  return {
    filters,
    page: Number.isInteger(page) && page > 0 ? page : 1,
    query: params.get('q') || '',
    sortBy,
    sortOrder,
  };
};

export const writeUrlState = ({ filters, page, query, sortBy, sortOrder }) => {
  const params = new URLSearchParams();

  if (page > 1) params.set('page', page);
  if (query) params.set('q', query);
  if (sortBy) {
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder || DEFAULT_SORT_ORDER);
  }

  Object.entries(FILTER_PARAMS).forEach(([key, param]) => {
    (filters?.[key] || []).forEach((value) =>
      params.append(`${param}[]`, value),
    );
  });

  const search = params.toString();

  // The hash is carried over rather than dropped: rewriting the URL on every
  // page, sort or filter change would otherwise silently discard any fragment
  // the user arrived with.
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${search ? `?${search}` : ''}${
      window.location.hash
    }`,
  );
};
